/* global JQuery */
import { Widget2 } from 'bambooo';
import { Map as OlMap, Overlay, View } from 'ol';
import LayerSwitcher from 'ol-layerswitcher';
import { fromLonLat } from 'ol/proj';
/**
 * BaseMap — domain-free OpenLayers map shell.
 *
 * Owns:
 *   - the {@link OlMap} instance + a sane default view (WGS84 input,
 *     EPSG:3857 display, multiWorld wrap),
 *   - the {@link LayerSwitcher} control (rebuilt every refresh so
 *     dynamically added layers always show up in the legend),
 *   - a single click/hover Overlay that turns into a Bootstrap popover
 *     when a feature with a `content` property is clicked,
 *   - the registry of attached {@link MapLayer} modules.
 *
 * Does NOT know anything about sightings, tours, ocean metrics, etc.
 * Domain layers come in via {@link addLayer}.
 */
export class BaseMap extends Widget2 {
    constructor(aelement) {
        super({
            element: jQuery('<div></div>')
        });
        /**
         * @protected
         */
        this._options = {};
        /**
         * Layer modules attached via {@link addLayer}. Tracked separately
         * from the OL layer collection so callers can detach by name and
         * we can re-attach (e.g. after a base-layer swap) without losing
         * configuration.
         * @protected
         */
        this._layers = [];
        this._element.appendTo(BaseMap._asParent(aelement));
    }
    /**
     * Resolve a parent element (bambooo component, jQuery, DOM node or
     * selector) to a jQuery handle the map container can be appended to.
     * @param {ComponentType} element
     * @return {JQuery}
     * @protected
     */
    static _asParent(element) {
        const maybe = element;
        if (maybe && typeof maybe.getElement === 'function') {
            return maybe.getElement();
        }
        return jQuery(element);
    }
    /**
     * Create the underlying OL map + optional controls. Must be called
     * once before any other method.
     */
    load(options) {
        var _a, _b;
        if (options !== undefined) {
            this._options = options;
        }
        const centerLonLat = (_a = this._options.initialCenterLonLat) !== null && _a !== void 0 ? _a : [11.030, 47.739];
        const zoom = (_b = this._options.initialZoom) !== null && _b !== void 0 ? _b : 2.2;
        this._map = new OlMap({
            target: this._element[0],
            view: new View({
                center: fromLonLat(centerLonLat),
                zoom,
                multiWorld: true
            })
        });
        if (this._options.withPopover !== false) {
            this._createPopover();
        }
        if (this._options.withLayerSwitcher !== false) {
            this._mountLayerSwitcher();
        }
    }
    /**
     * Tear down popover anchor + layers. The OL map itself is left for
     * the caller to drop (we keep parity with the legacy widget's
     * "unload only drops side effects" semantics).
     */
    unload() {
        jQuery('.popover').remove();
        if (this._popoverAnchor) {
            this._popoverAnchor.remove();
            this._popoverAnchor = undefined;
        }
        for (const layer of this._layers) {
            layer.detach();
        }
        this._layers = [];
    }
    /**
     * @returns the underlying OL Map, or undefined if {@link load} hasn't
     * been called yet.
     */
    getOlMap() {
        return this._map;
    }
    /**
     * Set the visible viewport. Pass a Coordinate already projected to
     * the view projection (EPSG:3857) — use `fromLonLat([lon, lat])` at
     * the call site.
     */
    setView(viewCenter = null, viewZoom = 12.5) {
        if (!this._map) {
            return;
        }
        const center = viewCenter !== null && viewCenter !== void 0 ? viewCenter : fromLonLat([-17.3340221, 28.0525008]);
        this._map.setView(new View({
            center,
            zoom: viewZoom,
            multiWorld: true
        }));
    }
    /**
     * Set the element height (px). The OL map needs an explicit height
     * to size its canvas — without this the map renders 0 px tall.
     */
    setHeight(height) {
        this._element.css({
            height: `${height}px`
        });
    }
    /**
     * Inform OL that the container size has changed (e.g. after a tab
     * switch or sidebar collapse). Cheap no-op when the map isn't
     * loaded yet.
     */
    updateSize() {
        var _a;
        (_a = this._map) === null || _a === void 0 ? void 0 : _a.updateSize();
    }
    /**
     * Attach a layer module. Idempotent: re-attaching a layer that's
     * already registered (same `getName()`) replaces it.
     */
    addLayer(layer) {
        if (!this._map) {
            return;
        }
        const existingIdx = this._layers.findIndex((l) => l.getName() === layer.getName());
        if (existingIdx !== -1) {
            const existing = this._layers[existingIdx];
            existing.detach();
            this._layers.splice(existingIdx, 1);
        }
        layer.attachTo(this);
        this._layers.push(layer);
        this.refreshLayerSwitcher();
    }
    /**
     * Detach the layer registered under `name`. No-op if no such layer.
     */
    removeLayerByName(name) {
        const idx = this._layers.findIndex((l) => l.getName() === name);
        if (idx === -1) {
            return;
        }
        const [layer] = this._layers.splice(idx, 1);
        layer.detach();
        this.refreshLayerSwitcher();
    }
    /**
     * Lookup an attached layer by its registered name. Useful when the
     * page needs to forward a domain call (e.g. addSighting, addArea)
     * onto a specific layer module.
     */
    getLayerByName(name) {
        return this._layers.find((l) => l.getName() === name);
    }
    /**
     * All currently attached layers in attach order.
     */
    getLayers() {
        return [...this._layers];
    }
    /**
     * Dispose the click-popover if one is currently open.
     */
    disposePopover(andRemove = false) {
        if (this._popover) {
            // Bootstrap 5 vanilla Popover instance — tear it down via dispose().
            this._popover.dispose();
            this._popover = undefined;
        }
        if (andRemove && this._popoverAnchor) {
            this._popoverAnchor.remove();
            this._popoverAnchor = undefined;
        }
    }
    /**
     * Rebuild the LayerSwitcher control. Called after any add/remove so
     * dynamic layers always appear in the legend. Public because some
     * layers (e.g. {@link MetricLayer}) swap their underlying OL layer
     * in place and need to re-register with the switcher.
     */
    refreshLayerSwitcher() {
        if (!this._map || this._options.withLayerSwitcher === false) {
            return;
        }
        if (this._layerSwitcher) {
            this._map.removeControl(this._layerSwitcher);
        }
        this._mountLayerSwitcher();
    }
    /**
     * @protected
     */
    _mountLayerSwitcher() {
        if (!this._map) {
            return;
        }
        this._layerSwitcher = new LayerSwitcher({
            reverse: true,
            groupSelectStyle: 'group'
        });
        this._map.addControl(this._layerSwitcher);
    }
    /**
     * Return the Bootstrap 5 Popover constructor from the global bundle
     * (loaded via the `bootstrap.bundle` script tag), or undefined if absent.
     * @protected
     */
    _getBsPopover() {
        var _a;
        return (_a = window.bootstrap) === null || _a === void 0 ? void 0 : _a.Popover;
    }
    /**
     * Wire the click/hover popover infrastructure. Looks up a `content`
     * property on the clicked feature and renders it as a Bootstrap
     * popover. `content` may be a string or a function returning a
     * string / DOM node — the same shape the legacy widget accepted, so
     * existing callers don't have to change.
     * @protected
     */
    _createPopover() {
        if (!this._map) {
            return;
        }
        this._popoverAnchor = jQuery('<div id="popup"></div>').appendTo(jQuery('body'));
        const overlay = new Overlay({
            element: this._popoverAnchor[0],
            offset: [10, 0],
            positioning: 'bottom-left'
        });
        this._map.addOverlay(overlay);
        this._map.on('click', (evt) => {
            if (!this._map) {
                return;
            }
            const feature = this._map.forEachFeatureAtPixel(evt.pixel, (f) => f);
            this.disposePopover();
            if (!feature) {
                return;
            }
            overlay.setPosition(evt.coordinate);
            const Popover = this._getBsPopover();
            if (!Popover || !this._popoverAnchor) {
                return;
            }
            /*
             * Bootstrap 5 removed the jQuery `$(el).popover()` plugin — use the
             * vanilla `new bootstrap.Popover(el, {...})` API (mirrors bambooo v2
             * BaseMap). `trigger: 'manual'` + explicit `.show()` keeps the
             * click-to-open behaviour; dispose() tears it down.
             */
            this._popover = new Popover(this._popoverAnchor[0], {
                html: true,
                /*
                 * Bootstrap 5 sanitizes popover HTML by default, which strips
                 * inline `style` attributes — that blanked the feature content
                 * (species badges carry their colour as `style="background:…"`
                 * and the label as `<b style="color:…">`, so sanitizing left an
                 * invisible white-on-transparent badge). We build this HTML
                 * ourselves, so disable the sanitizer.
                 */
                sanitize: false,
                trigger: 'manual',
                content: () => {
                    var _a;
                    const content = feature.get('content');
                    if (typeof content === 'string') {
                        return content;
                    }
                    else if (typeof content === 'function') {
                        const returnContent = content();
                        if (typeof returnContent === 'string') {
                            return returnContent;
                        }
                        else if (typeof returnContent === 'object') {
                            return (_a = jQuery(returnContent).html()) !== null && _a !== void 0 ? _a : '';
                        }
                    }
                    return 'None content found.';
                }
            });
            this._popover.show();
        });
        this._map.on('pointermove', (evt) => {
            if (!this._map) {
                return;
            }
            const pixel = this._map.getEventPixel(evt.originalEvent);
            const hit = this._map.hasFeatureAtPixel(pixel);
            const target = this._map.getTarget();
            if (target && typeof target !== 'string' && 'style' in target) {
                target.style.cursor = hit ? 'pointer' : '';
            }
        });
        this._map.on('movestart', () => {
            this.disposePopover();
        });
    }
}
